import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PrintAgentService } from '../../appServices/print-agent.service';
import { TenantResolver } from '../../appServices/tenant/tenant.resolver';
import { StorageService } from '../services/storage.service';

const PRINT_OUTBOX_KEY = 'tapgo_pos_print_outbox_v1';
const RETRY_DELAYS_MS = [1000, 3000, 10000, 30000];
const MAX_MISSING_ROLE_CHECKS = 6;

type AutomaticPrintRole = 'KOT' | 'BILLING';

interface AutomaticPrintEntry {
  id: string;
  tenant: string;
  orderId: number;
  role: AutomaticPrintRole;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  missingRoleChecks?: number;
}

interface AutomaticPrintState {
  version: 1;
  pending: AutomaticPrintEntry[];
  completed: Record<string, number>;
}

@Injectable({ providedIn: 'root' })
export class AutomaticPrintOutboxService {
  private state: AutomaticPrintState = {
    version: 1,
    pending: [],
    completed: {},
  };
  private loaded = false;
  private running = false;
  private retryTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private storage: StorageService,
    private tenantResolver: TenantResolver,
    private printAgent: PrintAgentService,
  ) {}

  async start(): Promise<void> {
    await this.load();
    void this.flush();
  }

  async planKOT(orderId: number): Promise<void> {
    await this.plan(orderId, 'KOT');
  }

  async planBilling(orderId: number): Promise<void> {
    await this.plan(orderId, 'BILLING');
  }

  private async plan(
    orderId: number,
    role: AutomaticPrintRole,
  ): Promise<void> {
    if (!Number.isInteger(orderId) || orderId <= 0) {
      throw new Error('A confirmed order id is required before printing.');
    }
    await this.load();
    const tenant = (await this.tenantResolver.getTenantName())?.trim();
    if (!tenant) {
      throw new Error('Tenant is required before planning a print.');
    }
    const id = this.buildId(tenant, orderId, role);
    if (this.state.completed[id] || this.state.pending.some((item) => item.id === id)) {
      return;
    }
    this.state.pending.push({
      id,
      tenant,
      orderId,
      role,
      createdAt: Date.now(),
      attempts: 0,
      nextAttemptAt: 0,
    });
    await this.persist();
    void this.flush();
  }

  private async flush(): Promise<void> {
    if (this.running) {
      return;
    }
    this.running = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    try {
      await this.load();
      const tenant = (await this.tenantResolver.getTenantName())?.trim();
      if (!tenant) {
        return;
      }
      const now = Date.now();
      const entry = this.state.pending.find(
        (item) => item.tenant === tenant && item.nextAttemptAt <= now,
      );
      if (!entry) {
        this.scheduleNext();
        return;
      }

      try {
        const printers = await firstValueFrom(this.printAgent.getPrinters());
        const configured = (printers || []).some(
          (printer) =>
            `${printer?.role || ''}`.trim().toUpperCase() === entry.role,
        );
        if (!configured) {
          entry.missingRoleChecks = (entry.missingRoleChecks || 0) + 1;
          if (entry.missingRoleChecks < MAX_MISSING_ROLE_CHECKS) {
            throw new Error(
              `Printer role ${entry.role} is temporarily unavailable.`,
            );
          }
        } else {
          entry.missingRoleChecks = 0;
          if (entry.role === 'KOT') {
            await this.printAgent.printKOT(entry.orderId, entry.id, true);
            await this.rememberLegacyPrintedOrder(entry.orderId);
          } else {
            await this.printAgent.printBill(entry.orderId, entry.id, true);
          }
        }
        await this.complete(entry);
      } catch (error) {
        entry.attempts += 1;
        entry.nextAttemptAt =
          Date.now() +
          RETRY_DELAYS_MS[
            Math.min(entry.attempts - 1, RETRY_DELAYS_MS.length - 1)
          ];
        await this.persist();
        console.error('Automatic print kept for retry', entry.id, error);
      }
      this.scheduleNext();
    } finally {
      this.running = false;
    }
  }

  private async complete(entry: AutomaticPrintEntry): Promise<void> {
    this.state.pending = this.state.pending.filter(
      (item) => item.id !== entry.id,
    );
    this.state.completed[entry.id] = Date.now();
    const completed = Object.entries(this.state.completed)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 500);
    this.state.completed = Object.fromEntries(completed);
    await this.persist();
  }

  private scheduleNext(): void {
    if (this.retryTimer || this.state.pending.length === 0) {
      return;
    }
    const nextAt = Math.min(
      ...this.state.pending.map((entry) => entry.nextAttemptAt || Date.now()),
    );
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      void this.flush();
    }, Math.max(250, nextAt - Date.now()));
  }

  private async rememberLegacyPrintedOrder(orderId: number): Promise<void> {
    const raw = await this.storage.get('printedOrderIds');
    const ids = raw ? (JSON.parse(raw) as number[]) : [];
    if (!ids.includes(orderId)) {
      ids.push(orderId);
      await this.storage.set('printedOrderIds', JSON.stringify(ids));
    }
  }

  private async load(): Promise<void> {
    if (this.loaded) {
      return;
    }
    const raw = await this.storage.getStrict(PRINT_OUTBOX_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AutomaticPrintState;
      if (
        parsed?.version !== 1 ||
        !Array.isArray(parsed.pending) ||
        !parsed.completed
      ) {
        throw new Error('Automatic print outbox is invalid.');
      }
      this.state = parsed;
    }
    this.loaded = true;
  }

  private async persist(): Promise<void> {
    await this.storage.set(PRINT_OUTBOX_KEY, JSON.stringify(this.state));
  }

  private buildId(
    tenant: string,
    orderId: number,
    role: AutomaticPrintRole,
  ): string {
    return `pos-auto-v1:${tenant}:${orderId}:${role}`;
  }
}
